/**
 * Auflösung der versionierten Einstellungen (AP-22-B).
 *
 * `PayrollSetting` hält je Schlüssel beliebig viele Sätze mit `validFrom`. Für
 * einen Stichtag ergibt sich daraus genau ein Wert je Schlüssel — und erst das
 * Ergebnis, ein flaches `Record<string, string>`, geht in `parseKonfig()`.
 *
 * Bewusst hier und nicht im Backend-Service: Die Regel entscheidet, mit welchen
 * Werten ein Monat gerechnet wurde. Sie muss ohne Datenbank testbar sein, sonst
 * ist eine Nachrechnung nicht überprüfbar.
 */
import { vorlageWerte, type KvVorlage } from './kv'

/** Eine Einstellungszeile, wie sie aus `payroll_settings` kommt. */
export interface EinstellungSatz {
  /** null = gilt für den ganzen Mandanten. */
  collectiveAgreementId: string | null
  key: string
  value: string
  /** ISO-Datum (YYYY-MM-DD). */
  validFrom: string
}

/**
 * Wählt je Schlüssel den für `stichtag` gültigen Wert.
 *
 * Drei Regeln, in dieser Reihenfolge:
 *
 * 1. **Nur Sätze mit `validFrom <= stichtag`** kommen infrage. Ein künftig
 *    gültiger Satz darf einen abgerechneten Monat nicht verändern — genau dafür
 *    ist die Versionierung da.
 * 2. **Spezifischer schlägt allgemeiner.** Existiert für einen Schlüssel ein Satz
 *    des übergebenen Kollektivvertrags, gewinnt dessen jüngster Satz. Erst wenn
 *    es keinen gibt, greift der jüngste mandantenweite Satz
 *    (`collectiveAgreementId = null`).
 *
 * Zu Regel 2: AP-22 §7.4 sagt „ein KV-eigener Eintrag übersteuert ihn", legt aber
 * nicht fest, was gilt, wenn der mandantenweite Satz das *neuere* `validFrom` hat.
 * Hier gewinnt die Spezifität, nicht das Datum — sonst hebt eine allgemeine
 * Änderung eine bewusst gesetzte KV-Ausnahme stillschweigend auf. In Stufe 1 ist
 * der Fall ohnehin unerreichbar, weil alle Sätze `null` tragen.
 *
 * 3. **Darunter liegt die KV-Vorlage** (AP-22 Teil 6): Wo der Mandant keinen eigenen Satz
 *    führt, gilt der Wert der Vorlage zum Stichtag — der Standard des Kollektivvertrags.
 *    Auch hier schlägt Spezifität das Datum: Ein eigener Wert bleibt, wenn die Vorlage zum
 *    Jahreswechsel einen neuen bekommt; die Maske zeigt dann beide.
 *
 * Ohne Vorlage enthält das Ergebnis nur, was der Mandant gesetzt hat — der Rest fällt in
 * `parseKonfig` auf die Bestandswerte des Rechenkerns.
 *
 * @param saetze  alle Sätze des Mandanten (ungefiltert, unsortiert)
 * @param stichtag ISO-Datum (YYYY-MM-DD)
 * @param collectiveAgreementId Kollektivvertrag oder null/undefined
 * @param vorlage KV-Vorlage, deren Werte unter den Sätzen des Mandanten liegen
 */
export function aufloesenEinstellungen(
  saetze: EinstellungSatz[],
  stichtag: string,
  collectiveAgreementId?: string | null,
  vorlage?: KvVorlage | null
): Record<string, string> {
  // Je Schlüssel den jüngsten gültigen Satz merken — getrennt nach KV-eigen und
  // mandantenweit, damit Regel 2 unabhängig vom Datum entscheiden kann.
  const kvEigen = new Map<string, EinstellungSatz>()
  const allgemein = new Map<string, EinstellungSatz>()

  for (const satz of saetze) {
    if (satz.validFrom > stichtag) continue // Regel 1

    const istKvEigen = satz.collectiveAgreementId !== null
    if (istKvEigen && satz.collectiveAgreementId !== (collectiveAgreementId ?? null)) {
      continue // Satz eines fremden Kollektivvertrags
    }

    const ziel = istKvEigen ? kvEigen : allgemein
    const bisher = ziel.get(satz.key)
    if (!bisher || satz.validFrom > bisher.validFrom) ziel.set(satz.key, satz)
  }

  const ergebnis: Record<string, string> = vorlage ? vorlageWerte(vorlage, stichtag) : {} // Regel 3
  for (const [key, satz] of allgemein) ergebnis[key] = satz.value
  for (const [key, satz] of kvEigen) ergebnis[key] = satz.value // Regel 2
  return ergebnis
}
